import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import Eyebrow from './Eyebrow';
import {
  marketingColors as C, marketingFonts as F, marketingDarkStyles, marketingDarkPalette as D,
} from '../../theme_landing_page_2';
import { marketingColors as Legacy } from '../../theme_landing_page';

interface PillarHeroProps {
  eyebrow: string;
  title: React.ReactNode;
  body: string;
  secondaryLabel: string;
  secondaryTo: string;
}

export const PillarHero: React.FC<PillarHeroProps> = ({ eyebrow, title, body, secondaryLabel, secondaryTo }) => (
  <Box component="header" sx={{ maxWidth: 960, mx: 'auto', px: 3, pt: { xs: '120px', md: '144px' }, pb: 2, textAlign: 'center' }}>
    <Eyebrow>{eyebrow}</Eyebrow>
    <Box component="h1" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2.6rem, 6vw, 4.5rem)', lineHeight: 1.06, letterSpacing: '-0.035em', my: '24px' }}>
      {title}
    </Box>
    <Box component="p" sx={{ maxWidth: '38rem', mx: 'auto', color: C.body, fontSize: { xs: 16, md: 18 } }}>{body}</Box>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4, flexWrap: 'wrap' }}>
      <Box
        component={RouterLink}
        to="/register"
        sx={{ display: 'inline-flex', alignItems: 'center', background: C.millosMint, color: C.ink, fontWeight: 600, borderRadius: '32px', px: 3, py: 1.75, '&:hover': { background: C.millosBlue, color: C.white } }}
      >
        Start free trial
      </Box>
      <Box
        component={RouterLink}
        to={secondaryTo}
        sx={{ display: 'inline-flex', alignItems: 'center', color: C.ink, border: `1px solid ${C.hairline}`, borderRadius: '32px', px: 3, py: 1.75, '&:hover': { background: C.panel } }}
      >
        {secondaryLabel}
      </Box>
    </Box>
  </Box>
);

const GlassPanel: React.FC<{ children: React.ReactNode; tilt?: 'a' | 'b' }> = ({ children, tilt = 'a' }) => (
  <Box
    sx={{
      position: 'relative',
      borderRadius: '24px',
      background: tilt === 'a' ? `linear-gradient(135deg,${C.tintBlue},${Legacy.tintViolet})` : `linear-gradient(135deg,${Legacy.tintViolet},${C.tintBlue})`,
      border: `1px solid ${C.glassBorder}`,
      padding: '24px',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ borderRadius: '16px', background: C.white, border: `1px solid ${C.hairline}`, padding: '18px' }}>
      {children}
    </Box>
  </Box>
);

interface FeatureRowProps {
  eyebrow: string;
  title: string;
  body: string;
  panel: React.ReactNode;
  imageSide: 'left' | 'right';
  tilt?: 'a' | 'b';
  textFlex?: string;
  panelFlex?: string;
}

export const PillarFeatureRow: React.FC<FeatureRowProps> = ({ eyebrow, title, body, panel, imageSide, tilt = 'a', textFlex = '1fr', panelFlex = '1fr' }) => {
  const textBlock = (
    <Box>
      <Eyebrow>{eyebrow}</Eyebrow>
      <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2rem,3.6vw,3rem)', lineHeight: 1.12, letterSpacing: '-0.03em', marginTop: '14px' }}>
        {title}
      </Box>
      <Box sx={{ marginTop: '16px', color: C.body, fontSize: 16, lineHeight: 1.65 }}>{body}</Box>
    </Box>
  );
  const panelBlock = <GlassPanel tilt={tilt}>{panel}</GlassPanel>;

  return (
    <Box component="section" sx={{ padding: { xs: '40px 24px', md: '40px 52px' } }}>
      <Box
        sx={{
          maxWidth: 1100,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: imageSide === 'left' ? `${panelFlex} ${textFlex}` : `${textFlex} ${panelFlex}` },
          gap: { xs: '32px', md: '56px' },
          alignItems: 'center',
        }}
      >
        {imageSide === 'left' ? (
          <>
            {panelBlock}
            {textBlock}
          </>
        ) : (
          <>
            {textBlock}
            {panelBlock}
          </>
        )}
      </Box>
    </Box>
  );
};

interface FeatureGridItem {
  title: string;
  body: string;
}

export const PillarFeatureGrid: React.FC<{ heading: string; iconBg: string; icon: React.ReactNode; items: FeatureGridItem[] }> = ({ heading, iconBg, icon, items }) => (
  <Box component="section" sx={{ padding: { xs: '48px 24px 64px', md: '56px 52px 96px' } }}>
    <Box sx={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center', marginBottom: '44px' }}>
      <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2rem,4vw,3.25rem)', letterSpacing: '-0.03em' }}>{heading}</Box>
    </Box>
    <Box sx={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3,1fr)' }, gap: 2 }}>
      {items.map((f) => (
        <Box key={f.title} sx={{ border: `1px solid ${C.hairline}`, borderRadius: '24px', bgcolor: C.mist, p: 3 }}>
          <Box sx={{ width: 46, height: 46, borderRadius: '13px', background: iconBg, display: 'grid', placeItems: 'center', marginBottom: '18px' }}>{icon}</Box>
          <Box component="h3" sx={{ fontFamily: F.ui, fontSize: 19, lineHeight: 1.3, margin: 0 }}>{f.title}</Box>
          <Box component="p" sx={{ mt: 1, mb: 0, color: C.body, fontSize: 14, lineHeight: 1.55 }}>{f.body}</Box>
        </Box>
      ))}
    </Box>
  </Box>
);

export const PillarClosingCta: React.FC<{ heading: string }> = ({ heading }) => (
  <Box component="section" sx={{ ...marketingDarkStyles, p: 1.5 }}>
    <Box sx={{ maxWidth: 1392, mx: 'auto', background: C.heroGradient, color: D.text, borderRadius: '32px', px: 3, py: { xs: 5, md: 8 }, textAlign: 'center' }}>
      <Eyebrow variant="onImage">Your next step</Eyebrow>
      <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2rem,4vw,3.3rem)', lineHeight: 1.1, mt: 3, mb: 2 }}>{heading}</Box>
      <Box component="p" sx={{ color: D.muted, m: 0 }}>Try both agents free for 1 week. No card required.</Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mt: 3 }}>
        <Box component={RouterLink} to="/register" sx={{ display: 'inline-flex', alignItems: 'center', background: D.accent, color: D.surface, fontWeight: 600, borderRadius: '32px', px: 3, py: 1.75, '&:hover': { background: D.blue } }}>Start free trial →</Box>
        <Box component={RouterLink} to="/book-a-call" sx={{ display: 'inline-flex', alignItems: 'center', color: D.text, border: `1px solid ${D.muted}`, borderRadius: '32px', px: 3, py: 1.75, '&:hover': { background: D.elevated } }}>Book a call</Box>
      </Box>
    </Box>
  </Box>
);
